import { Fragment } from 'react';
import type { CSSProperties, ReactNode } from 'react';
import { renderTextNode } from './marks.js';
import { collectText, type TipTapNode, type TipTapRenderOptions } from './types.js';

const HEADING_STYLES: Record<number, { size: number; weight: number; marginTop: number }> = {
  1: { size: 30, weight: 700, marginTop: 28 },
  2: { size: 24, weight: 700, marginTop: 24 },
  3: { size: 20, weight: 700, marginTop: 20 },
  4: { size: 17, weight: 700, marginTop: 18 },
  5: { size: 15, weight: 700, marginTop: 16 },
  6: { size: 14, weight: 700, marginTop: 14 },
};

export function renderNodes(nodes: TipTapNode[], options: TipTapRenderOptions, keyPrefix: string): ReactNode[] {
  return nodes.map((node, index) => renderNode(node, options, `${keyPrefix}-${index}`));
}

export function renderNode(node: TipTapNode, options: TipTapRenderOptions, key: string): ReactNode {
  switch (node.type) {
    case 'paragraph':
      return renderParagraph(node, options, key);
    case 'heading':
      return renderHeading(node, options, key);
    case 'bulletList':
      return (
        <ul key={key} style={listStyle(options, 'disc')}>
          {node.content ? renderNodes(node.content, options, key) : null}
        </ul>
      );
    case 'orderedList':
      return (
        <ol key={key} style={listStyle(options, 'decimal')}>
          {node.content ? renderNodes(node.content, options, key) : null}
        </ol>
      );
    case 'listItem':
      return (
        <li key={key} style={{ margin: '4px 0', lineHeight: 1.65 }}>
          {node.content ? renderNodes(node.content, options, key) : null}
        </li>
      );
    case 'taskList':
      return (
        <ul key={key} style={{ margin: '0 0 16px', paddingLeft: 4, listStyle: 'none', color: options.color }}>
          {node.content ? renderNodes(node.content, options, key) : null}
        </ul>
      );
    case 'taskItem':
      return renderTaskItem(node, options, key);
    case 'blockquote':
    case 'quote':
    case 'blockquoteFigure':
      return renderBlockquote(node, options, key);
    case 'codeBlock':
      return renderCodeBlock(node, options, key);
    case 'horizontalRule':
      return <hr key={key} style={{ border: 'none', borderTop: `1px solid ${options.color}1f`, margin: '20px 0' }} />;
    case 'hardBreak':
      return <br key={key} />;
    case 'image':
      return renderImage(node, options, key);
    case 'iframe':
    case 'video':
    case 'embed':
      return renderEmbed(node, options, key);
    case 'table':
      return renderTable(node, options, key);
    case 'tableRow':
      return <tr key={key}>{node.content ? renderNodes(node.content, options, key) : null}</tr>;
    case 'tableHeader':
      return renderTableCell(node, options, key, true);
    case 'tableCell':
      return renderTableCell(node, options, key, false);
    case 'details':
      return renderDetails(node, options, key);
    case 'detailsSummary':
      return <summary key={key} style={{ cursor: 'pointer', fontWeight: 600, padding: '6px 0' }}>{node.content ? renderNodes(node.content, options, key) : null}</summary>;
    case 'detailsContent':
      return <div key={key} style={{ padding: '6px 0 0 16px' }}>{node.content ? renderNodes(node.content, options, key) : null}</div>;
    case 'figcaption':
      return <figcaption key={key} style={{ fontSize: 12, color: `${options.color}99`, marginTop: 6, textAlign: 'center' }}>{node.content ? renderNodes(node.content, options, key) : null}</figcaption>;
    case 'columns':
      return <div key={key} style={{ display: 'grid', gridTemplateColumns: `repeat(${node.content?.length ?? 2}, 1fr)`, gap: 16, margin: '12px 0' }}>{node.content ? renderNodes(node.content, options, key) : null}</div>;
    case 'column':
      return <div key={key}>{node.content ? renderNodes(node.content, options, key) : null}</div>;
    case 'text':
      return renderTextNode(node, options, key);
    case 'doc':
      return <Fragment key={key}>{node.content ? renderNodes(node.content, options, key) : null}</Fragment>;
    default:
      return node.content ? <Fragment key={key}>{renderNodes(node.content, options, key)}</Fragment> : null;
  }
}

function renderParagraph(node: TipTapNode, options: TipTapRenderOptions, key: string): ReactNode {
  const textAlign = resolveTextAlign(node);
  return (
    <p
      key={key}
      style={{
        margin: '0 0 14px',
        lineHeight: 1.7,
        color: options.color,
        fontSize: 15,
        textAlign,
      }}
    >
      {node.content ? renderNodes(node.content, options, key) : null}
    </p>
  );
}

function renderHeading(node: TipTapNode, options: TipTapRenderOptions, key: string): ReactNode {
  const level = Math.min(Math.max(Number(node.attrs?.['level'] ?? 2), 1), 6);
  const style = HEADING_STYLES[level]!;
  const Tag = `h${level}` as 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6';
  const textAlign = resolveTextAlign(node);
  return (
    <Tag
      key={key}
      style={{
        margin: `${style.marginTop}px 0 10px`,
        color: options.color,
        fontSize: style.size,
        fontWeight: style.weight,
        lineHeight: 1.25,
        letterSpacing: level <= 2 ? -0.3 : -0.2,
        textAlign,
      }}
    >
      {node.content ? renderNodes(node.content, options, key) : null}
    </Tag>
  );
}

function renderBlockquote(node: TipTapNode, options: TipTapRenderOptions, key: string): ReactNode {
  return (
    <blockquote
      key={key}
      style={{
        margin: '16px 0',
        padding: '12px 16px',
        borderLeft: `3px solid ${options.primary}`,
        background: `${options.primary}0f`,
        borderRadius: 6,
        color: options.color,
      }}
    >
      {node.content ? renderNodes(node.content, options, key) : null}
    </blockquote>
  );
}

function renderCodeBlock(node: TipTapNode, options: TipTapRenderOptions, key: string): ReactNode {
  return (
    <pre
      key={key}
      style={{
        margin: '0 0 16px',
        padding: 14,
        background: '#0f172a',
        color: '#e2e8f0',
        borderRadius: options.radius,
        fontSize: 13,
        overflowX: 'auto',
        lineHeight: 1.55,
        fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, monospace',
      }}
    >
      <code>{collectText(node.content ?? [])}</code>
    </pre>
  );
}

function renderImage(node: TipTapNode, options: TipTapRenderOptions, key: string): ReactNode {
  const src = node.attrs?.['src'] as string | undefined;
  const alt = (node.attrs?.['alt'] as string | undefined) ?? '';
  if (!src) return null;
  return (
    <img
      key={key}
      src={src}
      alt={alt}
      style={{ maxWidth: '100%', height: 'auto', borderRadius: options.radius, margin: '12px 0', display: 'block' }}
    />
  );
}

function renderEmbed(node: TipTapNode, options: TipTapRenderOptions, key: string): ReactNode {
  const src = node.attrs?.['src'] as string | undefined;
  if (!src) return null;
  return (
    <div
      key={key}
      style={{ position: 'relative', paddingTop: '56.25%', margin: '16px 0', borderRadius: options.radius, overflow: 'hidden', background: '#000' }}
    >
      <iframe
        src={src}
        title=""
        allow="autoplay; fullscreen; picture-in-picture"
        allowFullScreen
        style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', border: 0 }}
      />
    </div>
  );
}

function renderTable(node: TipTapNode, options: TipTapRenderOptions, key: string): ReactNode {
  return (
    <div key={key} style={{ margin: '16px 0', overflowX: 'auto' }}>
      <table
        style={{
          borderCollapse: 'collapse',
          width: '100%',
          fontSize: 14,
          color: options.color,
          border: `1px solid ${options.color}1f`,
          borderRadius: 8,
          overflow: 'hidden',
        }}
      >
        <tbody>{node.content ? renderNodes(node.content, options, key) : null}</tbody>
      </table>
    </div>
  );
}

function renderTableCell(node: TipTapNode, options: TipTapRenderOptions, key: string, header: boolean): ReactNode {
  const Tag = header ? 'th' : 'td';
  const colspan = Number(node.attrs?.['colspan'] ?? 1);
  const rowspan = Number(node.attrs?.['rowspan'] ?? 1);
  return (
    <Tag
      key={key}
      colSpan={colspan > 1 ? colspan : undefined}
      rowSpan={rowspan > 1 ? rowspan : undefined}
      style={{
        border: `1px solid ${options.color}1f`,
        padding: '8px 12px',
        textAlign: header ? 'left' : 'left',
        fontWeight: header ? 600 : 400,
        background: header ? `${options.color}08` : 'transparent',
        verticalAlign: 'top',
      }}
    >
      {node.content ? renderNodes(node.content, options, key) : null}
    </Tag>
  );
}

function renderTaskItem(node: TipTapNode, options: TipTapRenderOptions, key: string): ReactNode {
  const checked = Boolean(node.attrs?.['checked']);
  return (
    <li key={key} style={{ display: 'flex', alignItems: 'flex-start', gap: 8, margin: '6px 0' }}>
      <input
        type="checkbox"
        checked={checked}
        readOnly
        style={{ marginTop: 4, accentColor: options.primary, cursor: 'default' }}
      />
      <div style={{ flex: 1, color: options.color, textDecoration: checked ? 'line-through' : 'none', opacity: checked ? 0.6 : 1 }}>
        {node.content ? renderNodes(node.content, options, key) : null}
      </div>
    </li>
  );
}

function renderDetails(node: TipTapNode, options: TipTapRenderOptions, key: string): ReactNode {
  return (
    <details
      key={key}
      open
      style={{
        margin: '16px 0',
        padding: '10px 14px',
        border: `1px solid ${options.color}1f`,
        borderRadius: 8,
        color: options.color,
      }}
    >
      {node.content ? renderNodes(node.content, options, key) : null}
    </details>
  );
}

function listStyle(options: TipTapRenderOptions, type: 'disc' | 'decimal'): CSSProperties {
  return {
    margin: '0 0 16px',
    paddingLeft: 24,
    color: options.color,
    listStyleType: type,
    lineHeight: 1.65,
  };
}

function resolveTextAlign(node: TipTapNode): CSSProperties['textAlign'] {
  const align = node.attrs?.['textAlign'] as string | undefined;
  if (align === 'center' || align === 'right' || align === 'justify') return align;
  return undefined;
}
